import { useParams, Link } from 'react-router-dom'
import { useEffect } from 'react'
import { trackBenchmarkViewed } from '@/lib/analytics-events'
import { ExternalLink, Download, ArrowLeft, FileText } from 'lucide-react'
import { useBenchmark, useBenchmarkPdfUrl } from '@/hooks/useBenchmarkQueries'
import { RelatedBenchmarks } from '@/components/benchmarks/RelatedBenchmarks'
import { ErrorState } from '@/components/shared/ErrorState'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDate } from '@/lib/utils'

export function BenchmarkDetailPage() {
  const { id } = useParams<{ id: string }>()

  // KAN-32 benchmark:viewed — fired regardless of load state so an error page
  // still counts the intent.
  useEffect(() => {
    trackBenchmarkViewed(id)
  }, [id])
  const { data, isLoading, isError, refetch } = useBenchmark(id)
  const pdfUrl = useBenchmarkPdfUrl(id, !!data?.pdfObjectKey)

  if (isLoading) {
    return (
      <main className="mx-auto flex max-w-4xl flex-col gap-4 p-6">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-40 w-full" />
      </main>
    )
  }

  if (isError || !data) {
    return (
      <main className="mx-auto max-w-4xl p-6">
        <ErrorState
          title="Could not load benchmark"
          message="The benchmark may have been removed or is not yet published."
          onRetry={() => refetch()}
        />
      </main>
    )
  }

  const onDownload = async () => {
    const r = await pdfUrl.refetch()
    if (r.data?.url) {
      window.location.href = r.data.url
    }
  }

  const paragraphs = data.description
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean)

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <nav aria-label="breadcrumb" className="text-xs text-muted-foreground">
        <Link
          to="/benchmarks"
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" aria-hidden />
          All benchmarks
        </Link>
      </nav>

      <header className="flex flex-col gap-3">
        <h1 className="text-3xl font-semibold leading-tight">{data.title}</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {data.publisherUrl ? (
            <a
              href={data.publisherUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium hover:underline"
            >
              {data.publisher}
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          ) : (
            <span className="font-medium">{data.publisher}</span>
          )}
          <Chip>{data.publisherType.replace(/_/g, ' ')}</Chip>
          <Chip>{data.reportType.replace(/_/g, ' ')}</Chip>
          <span className="text-muted-foreground">
            Published {formatDate(data.publishedAt)}
          </span>
        </div>
        {data.topics.length > 0 && (
          <div className="flex flex-wrap gap-1" aria-label="topics">
            {data.topics.map((t) => (
              <Badge key={t} variant="secondary" className="font-normal">
                {t}
              </Badge>
            ))}
          </div>
        )}
      </header>

      {/* Key-metric hero */}
      <section
        className="rounded-lg border bg-primary/5 p-6"
        aria-label="Key metric"
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Key metric
        </p>
        <p className="mt-1 text-2xl font-semibold leading-snug text-primary">
          {data.keyMetric}
        </p>
      </section>

      <section
        aria-label="Summary"
        className="flex flex-col gap-3 text-sm leading-relaxed"
      >
        {paragraphs.length > 0 ? (
          paragraphs.map((p, i) => <p key={i}>{p}</p>)
        ) : (
          <p className="text-muted-foreground">No summary provided.</p>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-2">
        <Button asChild variant="outline">
          <a
            href={data.reportUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2"
          >
            <ExternalLink className="h-4 w-4" aria-hidden />
            Open source
          </a>
        </Button>
        {data.pdfObjectKey && (
          <Button
            type="button"
            onClick={onDownload}
            disabled={pdfUrl.isFetching}
            className="inline-flex items-center gap-2"
          >
            <Download className="h-4 w-4" aria-hidden />
            {pdfUrl.isFetching ? 'Preparing…' : 'Download PDF'}
          </Button>
        )}
      </div>

      {data.pdfObjectKey && (
        <aside
          className="flex items-center gap-2 rounded-md border bg-muted/20 p-3 text-xs text-muted-foreground"
          aria-label="Attached document"
        >
          <FileText className="h-4 w-4" aria-hidden />
          <span>Official PDF attached · served via a short-lived signed link.</span>
        </aside>
      )}

      {/* SP5 Wave 3 — Related benchmarks (hybrid scoring, top 5). */}
      {id && <RelatedBenchmarks benchmarkId={id} />}
    </main>
  )
}

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">
      {children}
    </span>
  )
}
